import {
  Stack,
  VStack,
  Text,
  HStack,
  Button,
  Divider,
  Box,
  Heading,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { TrackedButton } from '$/components/common/Button/TrackedButton';
import { Icon } from '$/components/common/Icon';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import {
  HookFormCheckbox,
  type HookFormCheckboxProps,
} from '$/components/core/Form/HookFormCheckbox';
import {
  HookFormInput,
  type HookFormInputProps,
} from '$/components/core/Form/HookFormInput';
import { FileInput } from '$/components/core/LoadModal/FileInput';
import { StyledLink } from '$/components/core/StyledLink';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import { trackContentImpression, trackContentInteraction } from '$/logger';
import { registerUser } from '$/services/usecases/authentication';
import { createSchoolInvitationRequest } from '$/services/usecases/invitation';
import { emailFormatRegex, isPasswordValid } from '$/utils/regexUtils';

type SchoolRegistrationFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  acceptedTerms: boolean;
  schoolName: string;
  department: string;
  address: string;
  schoolUrl: string;
  phoneNumber: string;
  file: File | null;
  isExistingAccount: boolean;
};

const RegisterInput = (
  props: HookFormInputProps<SchoolRegistrationFormData>,
) => <HookFormInput<SchoolRegistrationFormData> {...props} />;

const RegisterCheckbox = (
  props: HookFormCheckboxProps<SchoolRegistrationFormData>,
) => <HookFormCheckbox<SchoolRegistrationFormData> {...props} />;

export const RegisterSchoolForm = () => {
  const { t } = useTranslation();
  const formMethods = useForm<SchoolRegistrationFormData>({
    mode: 'onTouched',
  });
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const user = useAuthenticationStore.useUser();
  const setModalType = useAuthenticationStore.useSetModalType();
  const setOtpEmail = useAuthenticationStore.useSetPendingAuthEmail();
  const setOtpToken = useAuthenticationStore.useSetPendingAuthToken();
  const closeAuthModal = useAuthenticationStore.useOnModalClose();
  const { cloudMessageTranslation } = useCloudMessageTranslation();

  useEffect(() => {
    if (user != null) {
      formMethods.reset({
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email ?? '',
        password: '',
        acceptedTerms: true,
        schoolName: '',
        department: '',
        address: '',
        schoolUrl: '',
        phoneNumber: '',
        isExistingAccount: true,
      });
    } else {
      formMethods.reset({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        acceptedTerms: false,
        schoolName: '',
        department: '',
        address: '',
        schoolUrl: '',
        phoneNumber: '',
        file: null,
        isExistingAccount: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (
      user?.role != null &&
      ['teacher', 'superTeacher', 'student'].includes(user.role)
    ) {
      trackContentImpression(
        'SchoolAccount',
        'RegisterSchool',
        'AlreadyRegistered',
      );
    }
  }, [user]);

  const isExistingUser = user != null;

  const onRegister = async (data: SchoolRegistrationFormData) => {
    if (data.file == null) return;
    let hasExistingAccount = isExistingUser;

    setIsLoading(true);

    if (!hasExistingAccount) {
      const { response: registerResponse, isSuccessful: isRegisterSuccessful } =
        await registerUser({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          password: data.password,
          acceptedTerms: data.acceptedTerms,
        });

      if (
        isRegisterSuccessful &&
        registerResponse.user != null &&
        registerResponse.otpToken
      ) {
        setOtpEmail(registerResponse.user.email);
        setOtpToken(registerResponse.otpToken);
      } else {
        if (registerResponse.message === 'userExisting') {
          hasExistingAccount = true;
        } else {
          setError(cloudMessageTranslation(registerResponse.message));
          return;
        }
      }
    }

    const { isSuccessful, httpStatusCode } =
      await createSchoolInvitationRequest({
        address: data.address,
        department: data.department,
        file: data.file,
        name: data.schoolName,
        phone: data.phoneNumber,
        requesterEmail: data.email,
        website: data.schoolUrl,
        isExistingAccount: hasExistingAccount,
      });

    if (isSuccessful) {
      toast(
        t('school.registerSchool.successToastTitle'),
        'success',
        t('school.registerSchool.successToastDescription'),
      );
    } else if (httpStatusCode === 409) {
      toast(
        t('school.registerSchool.conflictToastTitle'),
        'error',
        t('school.registerSchool.conflictToastDescription'),
      );
      setIsLoading(false);
      return;
    }

    if (!hasExistingAccount) {
      useAuthenticationStore.setState({ showRegisterSchoolInfo: true });
      setModalType('registerOtp');
    } else {
      closeAuthModal();
    }
    setIsLoading(false);
  };

  if (
    user?.role === 'teacher' ||
    user?.role === 'superTeacher' ||
    user?.role === 'student'
  ) {
    return (
      <VStack>
        <Text fontSize='sm' fontWeight='bold'>
          {t('school.registerSchool.form.alreadyRegistered')}
        </Text>
        <Button mt='4' onClick={closeAuthModal}>
          {t('general.close')}
        </Button>
      </VStack>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <VStack
        as='form'
        gap='10'
        onSubmit={formMethods.handleSubmit(onRegister)}
      >
        <Stack gap='6'>
          <Stack gap='4' fontSize='sm'>
            <Text>{t('dashboard.authentication.signUpWelcomeText')}</Text>
          </Stack>
          <Heading as='h2' fontSize='xl'>
            {t('school.registerSchool.form.teacherSectionTitle')}
          </Heading>
          {!isExistingUser && (
            <HStack gap='4'>
              <RegisterInput
                accessor='firstName'
                label={t('dashboard.authentication.firstNameLabel')}
                placeholder={t('dashboard.authentication.defaultPlaceholder', {
                  field: t('dashboard.authentication.firstNameLabel'),
                })}
                registerOptions={{
                  required: t(
                    'dashboard.authentication.errorMessages.isRequired',
                  ),
                }}
                hideErrorLabel
              />
              <RegisterInput
                accessor='lastName'
                label={t('dashboard.authentication.lastNameLabel')}
                placeholder={t('dashboard.authentication.defaultPlaceholder', {
                  field: t('dashboard.authentication.lastNameLabel'),
                })}
                registerOptions={{
                  required: t(
                    'dashboard.authentication.errorMessages.isRequired',
                  ),
                }}
                hideErrorLabel
              />
            </HStack>
          )}

          <RegisterInput
            type='email'
            accessor='email'
            label={t('dashboard.authentication.emailLabel')}
            placeholder={t('dashboard.authentication.defaultPlaceholder', {
              field: t('dashboard.authentication.emailLabel'),
            })}
            isDisabled={isExistingUser}
            registerOptions={{
              required: t('dashboard.authentication.errorMessages.isRequired'),
              pattern: {
                value: emailFormatRegex,
                message: t(
                  'dashboard.authentication.errorMessages.wrongFormat',
                ),
              },
            }}
            description={t('dashboard.authentication.errorMessages.emailRules')}
          />
          {!isExistingUser && (
            <>
              <RegisterInput
                type={showPassword ? 'text' : 'password'}
                accessor='password'
                label={t('dashboard.authentication.insertPasswordLabel')}
                placeholder={t('dashboard.authentication.defaultPlaceholder', {
                  field: t('dashboard.authentication.passwordLabel'),
                })}
                registerOptions={{
                  required: t(
                    'dashboard.authentication.errorMessages.isRequired',
                  ),
                  validate: {
                    passwordValid: (v) =>
                      isPasswordValid(String(v)) ||
                      t(
                        'dashboard.authentication.errorMessages.passwordRulesMismatch',
                      ),
                  },
                }}
                inputRightElement={
                  <Button
                    onClick={() => setShowPassword((state) => !state)}
                    variant='text'
                  >
                    <Icon
                      icon={showPassword ? 'visibility_on' : 'visibility_off'}
                    />
                  </Button>
                }
                description={t(
                  'dashboard.authentication.errorMessages.passwordRules',
                )}
              />
              <HStack alignItems='flex-start' gap='3'>
                <RegisterCheckbox
                  accessor='acceptedTerms'
                  registerOptions={{ validate: (val) => val === true }}
                  customLabelContent={
                    <Text mt='-1' fontSize='xs'>
                      <Trans
                        i18nKey='dashboard.authentication.createAccountLabel'
                        components={{
                          TermsLink: (
                            <StyledLink
                              color='primaryButton.background'
                              textDecoration='underline'
                              to='/tos'
                            >
                              Link
                            </StyledLink>
                          ),
                        }}
                      />
                    </Text>
                  }
                />
              </HStack>
            </>
          )}
          <Divider borderColor='border' />
          <Heading as='h2' fontSize='xl'>
            {t('school.registerSchool.form.schoolSectionTitle')}
          </Heading>
          <HStack gap='4'>
            <RegisterInput
              accessor='schoolName'
              label={t('school.registerSchool.form.schoolName')}
              placeholder={t('dashboard.authentication.defaultPlaceholder', {
                field: t('school.registerSchool.form.schoolName'),
              })}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
                maxLength: {
                  value: 70,
                  message: t('dashboard.authentication.errorMessages.tooLong', {
                    count: 70,
                  }),
                },
              }}
            />
            <RegisterInput
              accessor='department'
              label={t('school.registerSchool.form.department')}
              placeholder={t('dashboard.authentication.defaultPlaceholder', {
                field: t('school.registerSchool.form.department'),
              })}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
            />
          </HStack>
          <RegisterInput
            accessor='address'
            label={t('school.registerSchool.form.fullAddress')}
            placeholder={t('dashboard.authentication.defaultPlaceholder', {
              field: t('school.registerSchool.form.fullAddress'),
            })}
            registerOptions={{
              required: t('dashboard.authentication.errorMessages.isRequired'),
            }}
          />
          <HStack gap='4'>
            <RegisterInput
              accessor='schoolUrl'
              label={t('school.registerSchool.form.website')}
              placeholder={t('dashboard.authentication.defaultPlaceholder', {
                field: t('school.registerSchool.form.website'),
              })}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
            />
            <RegisterInput
              accessor='phoneNumber'
              label={t('school.registerSchool.form.phone')}
              placeholder={t('dashboard.authentication.defaultPlaceholder', {
                field: t('school.registerSchool.form.phone'),
              })}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
            />
          </HStack>
          <Box w='full'>
            <HStack justify='space-between' w='full'>
              <Text mb='2' color='text' fontSize='sm'>
                {t('school.registerSchool.form.uploadVerificationFile')}
              </Text>
              {fileError && (
                <Text mb='2' color='form.errorText' fontSize='sm'>
                  {t('school.registerSchool.form.fileUploadTooBigError')}
                </Text>
              )}
            </HStack>

            <FileInput
              selectedFile={formMethods.watch('file') ?? null}
              setSelectedFile={(value) => {
                if (value) {
                  trackContentInteraction(
                    'ClickButton',
                    'SchoolAccount',
                    'Registration',
                    'UploadDocument',
                  );
                }
                formMethods.setValue('file', value);
              }}
              maxSizeInMb={5}
              placeholder={t(
                'school.registerSchool.form.uploadVerificationFile',
              )}
              allowedExtensions={['pdf', 'jpg', 'jpeg', 'png']}
              onError={(error) => setFileError(error != null)}
            />
          </Box>
          <Text fontSize='xs'>
            <Trans
              i18nKey='dashboard.authentication.createAccountLabelPrivacy'
              components={{
                PrivacyLink: (
                  <StyledLink
                    color='primaryButton.background'
                    textDecoration='underline'
                    to='/privacy'
                  >
                    Link
                  </StyledLink>
                ),
              }}
            />
          </Text>
        </Stack>
        {error && (
          <Text color='form.errorText' fontWeight='bold'>
            {error}
          </Text>
        )}

        <Stack gap='6' w='full'>
          <TrackedButton
            fontSize='sm'
            isDisabled={
              !formMethods.formState.isValid ||
              formMethods.watch('file') == null
            }
            isLoading={isLoading}
            type='submit'
            contentName='SchoolAccount'
            contentPiece='Registration'
            contentTarget='NewAccount'
          >
            {t('dashboard.authentication.createAccountButton')}
          </TrackedButton>
        </Stack>
      </VStack>
    </FormProvider>
  );
};
